import * as React from 'react';
import { motion } from 'framer-motion';

interface Props {
  variant?: 'inline' | 'page';
  label?: string;
}

/**
 * Full-page loading uses a sweeping saffron arc that lives over a soft
 * pulsing aura. Inline loading uses three small dots staggered with
 * spring physics.
 */
export function LoadingState({ variant = 'inline', label = 'Loading' }: Props) {
  if (variant === 'page') {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-7">
        <SaffronArc />
        <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground/80">{label}</p>
      </div>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
      <InlineDots />
      <span>{label}</span>
    </span>
  );
}

/** Sweeping saffron arc on a faint pulsing aura — used for full-page loads. */
function SaffronArc() {
  const size = 72;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative inline-flex items-center justify-center">
      <motion.span
        aria-hidden
        className="absolute inset-0 rounded-full bg-saffron/15 blur-2xl"
        animate={{ scale: [1, 1.12, 1], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="relative">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="oklch(var(--border) / 0.8)"
          strokeWidth={stroke}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="oklch(0.78 0.16 78)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${c * 0.28} ${c}`}
          style={{ transformOrigin: '50% 50%' }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
        />
      </svg>
    </div>
  );
}

function InlineDots() {
  return (
    <span className="inline-flex items-center gap-[3px]">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="block size-1 rounded-full bg-current"
          animate={{ opacity: [0.25, 1, 0.25], y: [0, -1.5, 0] }}
          transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut', delay: i * 0.14 }}
        />
      ))}
    </span>
  );
}
